import Keycloak from 'keycloak-js';

import axios from 'axios';
import {
	getStfApiPath,
	SERVICE_STOREFRONT,
} from './application-urls';
import { portalHost } from './host';

export const fetchDataWithToken = async (
	{
		uri,
		method = 'GET',
		options = {},
		contentType = 'application/json',
		accept = 'application/json',
		eTag,
		lockField,
		operationId,
		token,
	},
) => {
	const eTagCheck = eTag
		? {
			'If-None-Match': `${eTag}`,
		}
		: {};
	const lockFieldCheck = lockField
		? {
			'Lock-Field': `${lockField}`,
		}
		: {};
	const operationIdCheck = operationId
		? {
			operationId: `${operationId}`,
		}
		: {};
	// Multipart form-data is set by body - need boundary to be set correctly
	const contentTypeCheck = contentType !== 'multipart/form-data'
		? {
			'Content-Type': `${contentType}`,
		}
		: {};

	const fullUri = `${portalHost}${uri}`;
	const data = await fetch(fullUri, {
		method,
		headers: {
			Authorization: `Bearer ${token}`,
			Accept: `${accept}`,
			...contentTypeCheck,
			...eTagCheck,
			...lockFieldCheck,
			...operationIdCheck,
		},
		cache: 'default',
		...options,
	})
		.then(response => {
			const {
				status,
				ok,
				redirected,
				url: responseURL,
			} = response;
			const contentLength = response.headers.get('Content-length');
			const resETag = response.headers.get('ETag');
			const resFilteredETag = response.headers.get('FilteredETag');
			const resContentType = response.headers.get('Content-Type');
			const resSchemaType = response.headers.get('Schema-Type');
			const resDisposition = response.headers.get('Content-Disposition');

			let parsedResponse = null;
			if (status === 403) {
				parsedResponse = Promise.resolve({
					detail: 'You do not have permission to access this.',
					supportMessage: 'Contact your administrator if you require access.',
					showExtraDetails: false,
				});
			} else if (redirected || (status === 204) || (status === 304) || (contentLength === '0')) {
				parsedResponse = Promise.resolve({});
			} else if (!ok && (!resContentType || resContentType.startsWith('text/html'))) {
				parsedResponse = Promise.resolve({
					detail: response.statusText,
					server: response.headers.get('Server'),
					code: `HTTP-${response.status}`,
					status: response.status,
					url: response.url,
				});
			} else if (resDisposition) {
				// Presence of a Content-Disposition file means we're downloading a file
				parsedResponse = response.blob();
			} else if (resContentType.startsWith('image/')) {
				parsedResponse = response.text();
			} else {
				parsedResponse = response.json();
			}

			const responseObject = parsedResponse.then(returnedData => ({
				status,
				body: returnedData,
				headers: response.headers,
				ok,
				eTag: resETag,
				filteredETag: resFilteredETag,
				redirected,
				responseURL,
				disposition: resDisposition,
				...resSchemaType ? { schemaType: resSchemaType } : {},
			}));
			return responseObject;
		});
	return data;
};

export const streamDataWithToken = async (
	{
		uri,
		method = 'GET',
		options = {},
		contentType = 'application/json',
		accept = 'application/json',
		eTag,
		lockField,
		operationId,
		token,
		parentLockField,
		callback,
		errorCallback,
	},
) => {
	const eTagCheck = eTag
		? {
			'If-None-Match': `${eTag}`,
		}
		: {};
	const lockFieldCheck = lockField
		? {
			'Lock-Field': `${lockField}`,
		}
		: {};
	const parentLockFieldCheck = parentLockField
		? {
			'Parent-Lock-Field': `${parentLockField}`,
		}
		: {};
	const operationIdCheck = operationId
		? {
			operationId: `${operationId}`,
		}
		: {};
	// Multipart form-data is set by body - need boundary to be set correctly
	const contentTypeCheck = contentType !== 'multipart/form-data'
		? {
			'Content-Type': `${contentType}`,
		}
		: {};

	/* eslint-disable no-restricted-syntax */
	return fetch(uri, {
		method,
		headers: {
			Authorization: `Bearer ${token}`,
			Accept: `${accept}`,
			...contentTypeCheck,
			...eTagCheck,
			...lockFieldCheck,
			...operationIdCheck,
			...parentLockFieldCheck,
		},
		cache: 'default',
		...options,
	}).then(res => {
		if (res.status >= 400) {
			// Do something different for an error.
			errorCallback(res);
			// Return an empty function so nothing happens.
			return (async () => {});
		}
		const stream = res.body;
		const decoder = new TextDecoder();

		// use for-await, but no explicit await at top-level
		let cache = '';
		return (async () => {
			for await (const chunk of stream) {
				cache += decoder.decode(chunk);
				if (cache.endsWith('\n')) {
					callback(cache);
					cache = '';
				}
			}
		})();
	});
};

const saveSucccess = function saveSucccess(
	keycloak, uri, method, options, contentType, accept, eTag, lockField,
) {
	return async function whenAthenticated(authenticated) {
		if (authenticated) {
			sessionStorage.setItem('store_kc_token', keycloak.token);
			sessionStorage.setItem('store_kc_refreshToken', keycloak.refreshToken);
			sessionStorage.setItem('store_kc_id_token', keycloak.idToken);
			// BMO alert('set token');

			// Once logged in, call loadData to prove access to keycloak.token
			// and to prove the ability to call a /ICS/rest/v2 endpoint
			return fetchDataWithToken({
				token: keycloak.token, uri, method, options, contentType, eTag, accept, lockField,
			});
		}
		// alert('not authenticated');
		return Promise.reject(new Error('fail'));
	};
};

function sleep(ms) {
	return new Promise(resolve => setTimeout(resolve, ms));
}

export const getApiPath = serviceName => {
	switch (serviceName) {
	case SERVICE_STOREFRONT: {
		return getStfApiPath();
	}
	default:
		return getStfApiPath();
	}
};

function parseJwt(token) {
	const base64Url = token.split('.')[1];
	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	const jsonPayload = decodeURIComponent(window.atob(base64).split('')
		// eslint-disable-next-line prefer-template
		.map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));

	return JSON.parse(jsonPayload);
}

let firstTime = true;

export const fetchData = async ({
	uri,
	method = 'GET',
	options = {},
	contentType = 'application/json',
	accept = 'application/json',
	eTag,
	lockField,
	operationId,
	serviceName = 'STF',
}) => {
	const keycloakURL = './config/keycloak.json';
	const keycloak = new Keycloak(keycloakURL);

	// This config should come from the keycloak.json file, stored in same directory as HTML pages
	// See Keycloak docs for more info: https://www.keycloak.org/docs/6.0/securing_apps/index.html#_javascript_adapter

	let token = sessionStorage.getItem('store_kc_token');
	let refreshToken = sessionStorage.getItem('store_kc_refreshToken');

	const apiUri = `${getApiPath(serviceName)}${uri}`;

	let refreshNeeded = false;

	if (token !== null && token.length !== 0) {
		const decodedToken = parseJwt(token);
		const { exp } = decodedToken;

		// Token check against expiry time

		if (exp !== null) {
			const grace = 90;
			const now = Date.now() / 1000;
			const timeLeft = exp - now - grace;
			// Timeleft is expiry minus grace period so we don't start a long task it near expiry

			if (timeLeft < 0) {
				refreshNeeded = true;
				token = null;
			}
		}
	} else if (firstTime) {
		// First time called since page load, helps refresh reload
		refreshNeeded = true;
	}

	if (refreshNeeded) {
		sessionStorage.setItem('store_kc_token', '');
		firstTime = false;
		refreshNeeded = false;

		return keycloak.init({
			onLoad: 'login-required', token, refreshToken, checkLoginIframe: false,
		})
			.then(saveSucccess(
				keycloak, apiUri, method, options, contentType, accept, eTag, lockField, operationId,
			),
			() => {
				sessionStorage.removeItem('store_kc_token');
			});
	}

	while (token == null || token.length === 0) {
		// Wait for another thread that's already looking to refresh a token
		/* eslint-disable no-await-in-loop */
		await sleep(200);
		refreshToken = sessionStorage.getItem('store_kc_refreshToken');
		token = sessionStorage.getItem('store_kc_token');
	}
	// Reuse the existing token
	return fetchDataWithToken({
		token, uri: apiUri, method, options, contentType, accept, eTag, lockField, operationId,
	});
};

export const streamData = async ({
	uri,
	method = 'GET',
	options = {},
	contentType = 'application/json',
	accept = 'application/json',
	eTag,
	lockField,
	operationId,
	serviceName = 'ICS',
	parentLockField,
	baseURL,
	callback,
	errorCallback = () => {},
}) => {
	const keycloakURL = './config/keycloak.json';
	const keycloak = new Keycloak(keycloakURL);

	// This config should come from the keycloak.json file, stored in same directory as HTML pages
	// See Keycloak docs for more info: https://www.keycloak.org/docs/6.0/securing_apps/index.html#_javascript_adapter

	let token = sessionStorage.getItem('store_kc_token');
	let refreshToken = sessionStorage.getItem('store_kc_refreshToken');

	let apiUri = `${getApiPath(serviceName)}${uri}`;

	if (baseURL !== undefined && baseURL !== null) {
		apiUri = `${baseURL}${apiUri}`;
	}

	let refreshNeeded = false;

	if (token !== null && token.length !== 0) {
		const decodedToken = parseJwt(token);
		const { exp } = decodedToken;

		// Token check against expiry time

		if (exp !== null) {
			const grace = 90;
			const now = Date.now() / 1000;
			const timeLeft = exp - now - grace;
			// Timeleft is expiry minus grace period so we don't start a long task it near expiry

			if (timeLeft < 0) {
				refreshNeeded = true;
				token = null;
			}
		}
	} else if (firstTime) {
		// First time called since page load, helps refresh reload
		refreshNeeded = true;
	}

	if (refreshNeeded) {
		sessionStorage.setItem('store_kc_token', '');
		firstTime = false;
		refreshNeeded = false;

		return keycloak.init({
			onLoad: 'login-required', token, refreshToken, checkLoginIframe: false,
		})
			.then(saveSucccess(
				keycloak, apiUri, method, options, contentType, accept, eTag, lockField, operationId,
			),
			() => {
				sessionStorage.removeItem('store_kc_token');
			});
	}

	while (token == null || token.length === 0) {
		// Wait for another thread that's already looking to refresh a token
		/* eslint-disable no-await-in-loop */
		await sleep(200);
		refreshToken = sessionStorage.getItem('store_kc_refreshToken');
		token = sessionStorage.getItem('store_kc_token');
	}
	// Reuse the existing token
	return streamDataWithToken({
		// eslint-disable-next-line max-len
		token, uri: apiUri, method, options, contentType, accept, eTag, lockField, operationId, parentLockField, callback, errorCallback,
	});
};

export const endSession = () => {
	axios
		.get('./config/keycloak.json')
		.then(res => {
			const keycloakData = res.data;
			const redirectUrl = `${window.location.origin}/store/`;
			const idToken = sessionStorage.getItem('store_kc_id_token');
			const logoutUrl = `${keycloakData['auth-server-url']}/realms/${keycloakData.realm}/protocol/openid-connect/logout?post_logout_redirect_uri=${redirectUrl}&id_token_hint=${idToken}`;

			sessionStorage.removeItem('store_kc_refreshToken');
			sessionStorage.removeItem('store_kc_token');
			sessionStorage.removeItem('store_kc_id_token');
			window.location.href = logoutUrl;
		})
		.catch(err => {
			/* eslint-disable-next-line  no-console */
			console.log(err);
		});
};

/*
	Add a stylesheet to a shadow DOM element for custom styling
*/
export const styleShadowDOM = (element, css) => {
	const shadowElement = element.shadowRoot;
	const styles = document.createElement('style');
	styles.type = 'text/css';
	styles.textContent = css;
	shadowElement.appendChild(styles);
};

/*
	Add a stylesheet to a shadow DOM element that's dynamically created
*/
export const styleDynamicShadowDOM = (selector, parent, css) => {
	if (parent) {
		const element = parent.querySelector(selector);
		if (element) {
			const style = document.createElement('style');
			style.type = 'text/css';
			style.textContent = css;
			if (!element.shadowRoot.styleSheets.length) {
				element.shadowRoot.prepend(style);
			}
		}
	}
};
